import {isEqual, isObject} from 'lodash';
import {useCallback, useState} from 'react';

import {InlineEditingError} from './inlineEditingErrorMessage';

export interface InlineEditingProps<T = string> {
  isEditing: boolean;
  editedValue: T;
  onEditStart: () => void;
  onEdit: (arg: T) => void;
  onEditSave: (arg: T) => void;
  onEditCancel: () => void;
  isSaving: boolean;
  error: InlineEditingError;
  shouldStartInEditMode?: boolean;
}

interface InlineEditingParams<T = string> {
  /** Current value of the inline edited field */
  currentAttributeValue: T;
  /** Handles saving the changes to the server */
  onSave: (arg: T) => Promise<void>;
  /** Whether we want the input field to start in edit mode */
  shouldStartInEditMode?: boolean;
  /** Optional function before saving that validates the input and throws an error  */
  validator?: (arg: T) => void;
  /** Optional to keep track of if the component is currently mounted */
  wasUnmounted?: boolean;
}

/**
 * React hook which enables a standard way for dealing with inline editing.
 */
export function useInlineEditing<T = string>(params: InlineEditingParams<T>) {
  const {currentAttributeValue, onSave, shouldStartInEditMode, validator, wasUnmounted} = params;
  const [isEditing, setIsEditing] = useState(shouldStartInEditMode || false);
  const [editedValue, setEditedValue] = useState(currentAttributeValue);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<InlineEditingError>(undefined);

  const onEditStart = () => {
    setEditedValue(currentAttributeValue);
    setIsEditing(true);
    setError(undefined);
  };

  const onEdit = (value: T) => setEditedValue(value);

  const onEditSave = useCallback(
    async (valueToSave: T) => {
      setIsEditing(false);
      setError(undefined);

      // If no changes have occured, return early
      if (checkAreValuesSame(valueToSave, currentAttributeValue)) {
        return;
      }

      // If we have a validator, validate the input and set the error, if any
      if (validator) {
        try {
          validator(valueToSave);
        } catch (err) {
          setIsEditing(true);
          setError(err);
          return;
        }
      }

      setIsSaving(true);
      try {
        await onSave(valueToSave);
      } catch (err) {
        setError(err);
        setIsEditing(true);
      }
      // If the save resulted in the component not being mounted anymore, we should not set state
      if (!wasUnmounted) {
        setIsSaving(false);
      }
    },
    [currentAttributeValue, onSave, validator, wasUnmounted],
  );

  const onEditCancel = () => {
    setIsEditing(false);
    setEditedValue(currentAttributeValue); // Discard any edits
  };

  return {
    isEditing,
    editedValue,
    onEditStart,
    onEdit,
    onEditSave,
    onEditCancel,
    isSaving,
    error,
    shouldStartInEditMode,
  };
}

/*
 * Helpers.
 */

function checkAreValuesSame<T>(firstValue: T, secondValue: T): boolean {
  if (isObject(firstValue)) {
    return isEqual(firstValue, secondValue);
  }
  return firstValue === secondValue;
}
